/**
 * The frame around the back-office screens.
 *
 * WHY A SIDEBAR AND NOT A HEADER
 * ------------------------------
 * There are seven destinations here and Phase 4 adds several more. A row of
 * them across the top starts scrolling sideways, which is the worst navigation
 * there is — you cannot see where you can go.
 *
 * It also spends the right dimension. A laptop or a landscape tablet has
 * horizontal room and very little vertical; a header takes 56px from the
 * scarce one on every screen, and a sidebar takes from the plentiful one.
 *
 * Collapses to icons for anyone who wants the width back, and remembers that
 * choice — it is a preference about a person's own screen, not something worth
 * a round trip to the server.
 */
import { useState } from 'react';
import { PinPanel } from '../../features/settings/PinPanel.jsx';
import { NavLink, Outlet } from 'react-router-dom';

import { useAuth } from '../../stores/auth.js';
import { SyncIndicator } from './SyncIndicator.jsx';
import { NAV } from './nav.js';
import { ChevronIcon } from './icons.jsx';
import logo from '../../assets/logo.png';
import { OnlineOrders } from '../../features/online-orders/OnlineOrders.jsx';

const COLLAPSED_KEY = 'foodrun.nav.collapsed';

function remembered() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function BackOfficeShell() {
  const [collapsed, setCollapsed] = useState(remembered);

  const user = useAuth((s) => s.user);
  const can = useAuth((s) => s.can);
  const signOut = useAuth((s) => s.signOut);
  // Anybody who approves anything at the till needs to be able to set the PIN
  // they approve it with, from the machine they are standing at.
  const [changingPin, setChangingPin] = useState(false);

  const visible = NAV.filter((item) => !item.permission || can(item.permission));

  function toggle() {
    setCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(COLLAPSED_KEY, String(next));
      } catch {
        /* private mode — the choice lasts until the tab closes */
      }
      return next;
    });
  }

  return (
    <div className="flex min-h-dvh">
      <nav
        aria-label="Main"
        className={`sticky top-0 flex h-dvh shrink-0 flex-col border-r border-rule bg-surface transition-[width] ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        <div className="flex h-14 items-center border-b border-rule px-3">
          {collapsed ? (
            <span className="mx-auto text-lg font-bold text-brand-400" aria-hidden="true">
              FR
            </span>
          ) : (
            <img src={logo} alt="Food Run" className="h-7 w-auto" width="512" height="97" />
          )}
        </div>

        <ul className="flex-1 space-y-0.5 overflow-y-auto p-2">
          {visible.map(({ to, label, Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                // The label is announced even when it is not drawn, so a
                // collapsed sidebar is not a row of unnamed buttons.
                title={collapsed ? label : undefined}
                className={({ isActive }) =>
                  `flex h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-brand-400/15 text-brand-300'
                      : 'text-ink-2 hover:bg-surface-3 hover:text-ink'
                  } ${collapsed ? 'justify-center px-0' : ''}`
                }
              >
                <Icon className="size-5 shrink-0" />
                <span className={collapsed ? 'sr-only' : 'truncate'}>{label}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="border-t border-rule p-2">
          <button
            onClick={toggle}
            aria-expanded={!collapsed}
            className={`flex h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-ink-3 hover:bg-surface-3 hover:text-ink-2 ${
              collapsed ? 'justify-center px-0' : ''
            }`}
          >
            <ChevronIcon className={`size-5 shrink-0 ${collapsed ? 'rotate-180' : ''}`} />
            <span className={collapsed ? 'sr-only' : ''}>Collapse</span>
          </button>
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 items-center justify-end gap-3 border-b border-rule bg-surface-2/95 px-4 backdrop-blur">
          <SyncIndicator />
          <span className="text-sm text-ink-2">{user?.firstName ?? user?.email}</span>
          <button
            onClick={() => setChangingPin(true)}
            className="rounded-lg px-3 py-2 text-sm font-medium text-ink-2 hover:bg-surface-3"
          >
            My PIN
          </button>
          <button
            onClick={signOut}
            className="rounded-lg px-3 py-2 text-sm font-medium text-ink-2 hover:bg-surface-3"
          >
            Sign out
          </button>
        </header>

        {changingPin && (
          <PinPanel pinSet={user?.pinSet} onClose={() => setChangingPin(false)} />
        )}

        <main className="flex-1">
          <Outlet />
          {/* Website orders waiting for an answer, wherever the cashier happens to be. */}
          <OnlineOrders />
        </main>
      </div>
    </div>
  );
}
